/**
 * Materiais complementares da aula — lâmina do produto, PDF da aula, links.
 * Espelha `catalog/domain/materials.py`: as regras daqui avisam antes do
 * envio; quem decide e recusa continua sendo a API.
 */
import type { components } from '@/core/http';

export type LessonMaterial = components['schemas']['LessonMaterialOut'];
export type MaterialTicket = components['schemas']['MaterialUploadTicketOut'];

/** Espelha `MATERIAL_TYPES`: tipo → extensões que o navegador pode trazer. */
export const MATERIAL_TYPES: Record<string, readonly string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

/** Espelha `MAX_MATERIAL_BYTES`, `MATERIAL_TITLE_MAX_LENGTH` e `MATERIALS_PER_LESSON_MAX`. */
export const MAX_MATERIAL_BYTES = 100 * 1024 ** 2;
export const MATERIAL_TITLE_MAX = 200;
export const MATERIALS_PER_LESSON_MAX = 30;
export const URL_MAX = 2000;

/** O `accept` do seletor de arquivos: os tipos e as extensões. */
export const MATERIAL_ACCEPT = Object.entries(MATERIAL_TYPES)
  .flatMap(([type, exts]) => [type, ...exts])
  .join(',');

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
};

/**
 * O tipo que vai para a API (e no cabeçalho do envio): o do navegador ou,
 * quando ele não sabe (comum com .doc e .xls no Windows), o da extensão.
 */
export function materialContentType(file: { name: string; type: string }): string | null {
  if (file.type in MATERIAL_TYPES) return file.type;
  const ext = extensionOf(file.name);
  const found = Object.entries(MATERIAL_TYPES).find(([, exts]) => exts.includes(ext));
  return found?.[0] ?? null;
}

/** Por que o arquivo não pode ser enviado, ou `null`. */
export function materialProblem(file: { name: string; type: string; size: number }): string | null {
  if (!materialContentType(file))
    return 'Formato não aceito. Envie PDF, imagem (PNG, JPG, WebP), Word, PowerPoint ou Excel.';
  if (file.size <= 0) return 'O arquivo está vazio.';
  if (file.size > MAX_MATERIAL_BYTES) return 'O arquivo passa de 100 MB.';
  return null;
}

/** Título inicial a partir do nome do arquivo: sem extensão, sem "_" — editável depois. */
export function titleFromFilename(name: string): string {
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const title = stem.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim() || 'Material';
  return title.slice(0, MATERIAL_TITLE_MAX);
}

/** Espelha a regra do link: endereço completo e https. */
export function linkProblem(raw: string): string | null {
  const url = raw.trim();
  if (!url) return 'Informe o endereço.';
  if (url.length > URL_MAX) return `O link pode ter até ${URL_MAX} caracteres.`;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'Informe o endereço completo, começando com https://';
  }
  if (parsed.protocol !== 'https:' || !parsed.hostname || /\s/.test(url))
    return 'Use um endereço seguro, começando com https://';
  return null;
}

export function materialTitleProblem(raw: string): string | null {
  const title = raw.replace(/\s+/g, ' ').trim();
  if (!title) return 'Dê um título ao material.';
  if (title.length > MATERIAL_TITLE_MAX) return `O título pode ter até ${MATERIAL_TITLE_MAX} caracteres.`;
  return null;
}

/** Família do arquivo — decide o ícone e o rótulo. */
export type MaterialFamily = 'pdf' | 'image' | 'doc' | 'slides' | 'sheet' | 'link' | 'file';

const FAMILY_BY_EXT: Record<string, MaterialFamily> = {
  '.pdf': 'pdf',
  '.png': 'image',
  '.jpg': 'image',
  '.jpeg': 'image',
  '.webp': 'image',
  '.doc': 'doc',
  '.docx': 'doc',
  '.ppt': 'slides',
  '.pptx': 'slides',
  '.xls': 'sheet',
  '.xlsx': 'sheet',
};

export function materialFamily(m: Pick<LessonMaterial, 'kind' | 'content_type'>): MaterialFamily {
  if (m.kind === 'link') return 'link';
  const ext = MATERIAL_TYPES[m.content_type ?? '']?.[0];
  return (ext && FAMILY_BY_EXT[ext]) || 'file';
}

export const MATERIAL_FAMILY_LABELS: Record<MaterialFamily, string> = {
  pdf: 'PDF',
  image: 'Imagem',
  doc: 'Word',
  slides: 'PowerPoint',
  sheet: 'Excel',
  link: 'Link',
  file: 'Arquivo',
};

/** "850 KB", "2,3 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024 ** 2) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1).replace('.', ',')} MB`;
}

/** Os materiais da aula, na ordem — a lista do curso vem com todas as aulas misturadas. */
export function materialsOfLesson(
  all: readonly LessonMaterial[] | undefined,
  lessonId: string,
): LessonMaterial[] {
  return (all ?? []).filter((m) => m.lesson_id === lessonId).sort((a, b) => a.position - b.position);
}

/** A lista do curso com os materiais de uma aula trocados pelos que a API devolveu. */
export function withLessonMaterials(
  all: readonly LessonMaterial[] | undefined,
  lessonId: string,
  lesson: readonly LessonMaterial[],
): LessonMaterial[] {
  return [...(all ?? []).filter((m) => m.lesson_id !== lessonId), ...lesson];
}
